import {
    ACTION_TO_REPORT_POST,
    ACTION_TO_SEND_HEART,
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_POST,
    EXPLAIN_POST_DELETION,
    FORUM_TURNED_OFF_NOTIFICATIONS_FOR_NEW_ANSWERS,
    FORUM_TURNED_ON_NOTIFICATIONS_FOR_NEW_ANSWERS,
} from 'mk/autogenerated/translations/sagas.cbc7e522cbba7970cc74784467100ab6'
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { forum_api_delete_post_url, forum_api_read_url, wall_api_set_follow_url } from 'mk/urls/functions';
import { CommentEntity } from 'mk2/apps/blogs/schemas';
import {
    FORUM_DELETE_POST,
    FORUM_POST_OPEN_COMPLAINT,
    FORUM_POST_SEND_HEART,
    FORUM_POST_TOGGLE_NOTIFICATIONS,
    FORUM_READ_FEED,
    ForumDeletePostAction,
    forumDeletePostApi,
    forumPostToggleNotificationsActions,
    ForumReadFeedAction,
    forumReadFeedSave,
    PostOpenComplaintAction,
    PostToggleNotificationsAction,
} from 'mk2/apps/forum/actions';
import { PostDetailPageState, PostDetailState } from 'mk2/apps/forum/containers/PostDetail/PostDetail.reducers';
import {
    getForumPostDetailPageState,
    getForumPostDetailState,
} from 'mk2/apps/forum/containers/PostDetail/PostDetail.selectors';
import { MessageSchema, PostEntity, PostSchema } from 'mk2/apps/forum/schemas';
import * as forumUrls from 'mk2/apps/forum/urls';
import mailSagas from 'mk2/apps/mail/sagas';
import { complaintToggle } from 'mk2/containers/Complaint/Complaint.actions';
import { handleXHRPostErrorSaga, normalizeError, XHRAction, XHRPostError } from 'mk2/helpers/api';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { AppState, getRequestUser } from 'mk2/reducers';
import { checkIfUserIsLoggedIn } from 'mk2/sagas/auth';
import { getDenormalizedEntities, getDenormalizedEntity } from 'mk2/selectors';
import { normalize } from 'normalizr';
import { push } from 'react-router-redux';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('forum/sagas');

interface ApiResponse {
}

function* readSave({ feed, xhr }: ForumReadFeedAction & XHRAction) {
    yield put(forumReadFeedSave.request(feed));

    try {
        const response: ApiResponse = yield call(() => xhr.post(forum_api_read_url(feed)));
        yield put(forumReadFeedSave.success(feed));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(forumReadFeedSave.failure(feed, normalizeError(error)));
    }
}

interface ToggleNotificationsApiResponse {
    body: {
        follow: boolean;
    };
}

function* toggleNotifications({ username, channelName, channelId, follow, xhr }: PostToggleNotificationsAction & XHRAction) {
    yield put(forumPostToggleNotificationsActions.request());

    try {
        const response: ToggleNotificationsApiResponse = yield call(
            () => xhr.post(wall_api_set_follow_url(username), { channelName, channelId, follow }),
        );

        yield put(forumPostToggleNotificationsActions.success(response.body.follow, channelId));
        if (response.body.follow) {
            yield showSuccessToast(FORUM_TURNED_ON_NOTIFICATIONS_FOR_NEW_ANSWERS);
        } else {
            yield showSuccessToast(FORUM_TURNED_OFF_NOTIFICATIONS_FOR_NEW_ANSWERS);
        }

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(forumPostToggleNotificationsActions.failure(normalizeError(error)));

    }
}

interface ApiResponseDeletePost {
    body: {
        post: PostEntity;
    };
}

function* deletePost({ id, xhr }: ForumDeletePostAction & XHRAction) {
    const requestUser = yield select(getRequestUser);
    const post: PostEntity = yield select((state: AppState) => getDenormalizedEntity<PostEntity>(state, PostSchema, id));
    const isAuthor = isAuthenticatedAs(requestUser, post.author);
    let reason;

    if (isAuthor) {
        if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_POST)) {
            return;
        }
    } else { // admin or moderator
        reason = window.prompt(EXPLAIN_POST_DELETION);
        if (reason === null) {
            return;
        }
    }

    yield put(forumDeletePostApi.request(id));

    try {
        const response: ApiResponseDeletePost = yield call(
            () => xhr.post(forum_api_delete_post_url(id), isAuthor ? {} : { comment: reason }),
        );

        // Mark all comments for this post as deleted - so that visually change
        let comments: CommentEntity[] = [];
        const postState: PostDetailState = yield select(getForumPostDetailState, post.topic.slug, post.slug);
        if (postState && postState.numberOfPages) {
            for (let page = 1; page <= postState.numberOfPages; page++) {
                const postPageState: PostDetailPageState =
                    yield select(getForumPostDetailPageState, post.topic.slug, post.slug, page);
                const pageComments: CommentEntity[] =
                    yield select(getDenormalizedEntities, MessageSchema, postPageState.commentIds);
                comments = comments.concat(pageComments);
            }
        }

        comments = comments.map((c) => (c.deletedAt ? c : {
            ...c,
            deletedAt: response.body.post.deletedAt,
        }));

        const normalizedResponse = normalize({
            post: response.body.post,
            comments,
        }, {
            post: PostSchema,
            comments: [MessageSchema],
        });

        yield put(forumDeletePostApi.success(id, normalizedResponse));

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(forumDeletePostApi.failure(id, normalizeError(error)));
    }
}

function* postOpenComplaint({ postId, topicSlug, postSlug }: PostOpenComplaintAction) {

    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_REPORT_POST))) {
        return;
    }

    yield put(complaintToggle('forum-thread', postId, true));

    // go to post detail
    const gotoUrl = url(forumUrls.forumPostDetail, {topicSlug, postSlug});
    yield put(push(gotoUrl));
}

function* postSendHeart() { /* this saga runs only in case the user is not logged in. Else the send heart function is provided via url redirection */

    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_SEND_HEART))) {
        return;
    }

}

export default function* root() {
    yield all([
        takeLatest(FORUM_READ_FEED, readSave),
        takeEvery(FORUM_POST_TOGGLE_NOTIFICATIONS, toggleNotifications),
        takeEvery(FORUM_POST_OPEN_COMPLAINT, postOpenComplaint),
        takeEvery(FORUM_POST_SEND_HEART, postSendHeart),
        takeEvery(FORUM_DELETE_POST, deletePost),

        fork(mailSagas),
    ]);
}
